import { Close } from '@mui/icons-material';
import { Dialog, IconButton, Skeleton } from '@mui/material';
import type React from 'react';
import { PreviewProxyStyle, PreviewProxyWrapper } from 'src/components/CommonStyles/Proxy/PreviewStyles';
import PDFPreview from 'src/components/PDF/PDFPreview';
import { IMAGE, PDF } from 'src/constants';
import {
  ImagePreview,
  useStyles,
} from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/AkomiMediaCard/MediaCardPreviewModal';
import MediaUnavailable from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/AkomiMediaCard/MediaUnavailable';

const SAMPLE_DOWNLOAD_LINK = 'https://www.sharedfilespro.com/shared-files/38/?sample.pdf&download=1';

interface MediaPreviewRouterProps {
  mimetype: string | undefined;
  sharedPreviewUrl: string;
}

const MediaPreviewRouter: React.FC<MediaPreviewRouterProps> = ({ mimetype, sharedPreviewUrl }) => {
  if (mimetype?.includes(IMAGE)) {
    return <ImagePreview src={sharedPreviewUrl} />;
  }
  if (mimetype?.includes(PDF)) {
    return <PDFPreview pdfUrl={sharedPreviewUrl} downloadLink={SAMPLE_DOWNLOAD_LINK} isOnMediaPage={false} />;
  }
  return (
    <PreviewProxyStyle>
      <PreviewProxyWrapper>
        <MediaUnavailable text={'Unsupported Mimetype'} />
      </PreviewProxyWrapper>
    </PreviewProxyStyle>
  );
};

interface SharedPageModalProps {
  mimetype: string | undefined;
  onClose: () => void;
  sharedPreviewUrl: string;
  showPreview: boolean;
}

const SharedPageModal: React.FC<SharedPageModalProps> = ({ mimetype, onClose, sharedPreviewUrl, showPreview }) => {
  // Please keep shared modal separated from other library modals as in
  // other modals we are sending the url search params to opensearch to get the results
  const classes = useStyles();

  const onClickClose = (): void => {
    onClose();
  };

  if (!sharedPreviewUrl && showPreview) {
    return <div>Error... </div>;
  }

  return (
    <Dialog
      open={true}
      onClose={onClickClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'transparent',
          overflow: 'hidden',
          boxShadow: 'none',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.92)' : 'rgba(0, 0, 0, 0.85)'),
          },
        },
      }}
    >
      <IconButton
        className={classes.closeIcon}
        onClick={onClickClose}
        size="large"
        sx={{ color: (theme) => theme.palette.common.white }}
      >
        <Close />
      </IconButton>
      {!showPreview ? (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '60%',
            height: '60%',
            borderRadius: 2,
            bgcolor: (theme) => theme.palette.action.disabledBackground,
          }}
        />
      ) : (
        <MediaPreviewRouter mimetype={mimetype} sharedPreviewUrl={sharedPreviewUrl} />
      )}
    </Dialog>
  );
};

export default SharedPageModal;
